import React, { useMemo, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import {
  useUsageSummary,
  useUsageTimeline,
  useDailyUsage,
} from '../../../../api/hooks/settings.hook';
import { useActions } from '../../../../api/hooks/actions.hook';
import type { Action } from '../../../../api/services/action';
import { useWorkspace } from '../../../../components/Workspace/artifacts';
import StatusBadge from '../../../../components/common/StatusBadge';
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Tooltip,
} from '../../../../components/design-system';
import {
  formatDateTime,
  InfoTile,
  getActionActors,
  buildSearchIndex,
  AuditLogTimeline,
  STATUS_LABELS,
  STATUS_TONES,
  STATUS_FILTERS,
  FALLBACK_ACTOR,
  type StatusFilterValue,
} from './shared';
import { DailyUsageLineChart, DailyUsageScatterChart } from './ObservabilityCharts';

export const ObservabilityConfiguration = () => {
  const usage = useUsageSummary();
  const timeline = useUsageTimeline();
  const { state: workspaceState } = useWorkspace();

  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');
  const [actorFilter, setActorFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(null);

  const dailyUsage = useDailyUsage(selectedDay);
  const { data = [], isLoading, isFetching, isError, refetch } = useActions(statusFilter);

  const actorOptions = useMemo(() => {
    const actors = new Set<string>();
    data.forEach((action) => {
      getActionActors(action).forEach((actor) => actors.add(actor));
    });
    return Array.from(actors).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const typeOptions = useMemo(() => {
    const types = new Set<string>();
    data.forEach((action) => types.add(action.action_type));
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredActions = useMemo(() => {
    return data.filter((action) => {
      if (actorFilter !== 'all' && !getActionActors(action).includes(actorFilter)) return false;
      if (typeFilter !== 'all' && action.action_type !== typeFilter) return false;
      if (normalizedSearch) {
        const haystack = buildSearchIndex(action);
        if (!haystack.includes(normalizedSearch)) return false;
      }
      return true;
    });
  }, [data, actorFilter, typeFilter, normalizedSearch]);

  const getActionName = (action: Action): string => {
    const payload = (action.payload || {}) as Record<string, unknown>;
    if (typeof payload.name === 'string' && payload.name.trim()) return payload.name;
    if (typeof payload.title === 'string' && payload.title.trim()) return payload.title;
    const subject = (payload as { subject?: unknown }).subject;
    if (typeof subject === 'string' && subject.trim()) return subject;
    const fileName = (payload as { file_name?: unknown }).file_name;
    if (typeof fileName === 'string' && fileName.trim()) return fileName;
    if (action.action_type === 'database') {
      const operation =
        typeof payload.operation === 'string' && payload.operation.trim()
          ? payload.operation
          : 'query';
      const table = (payload as { table?: unknown }).table;
      const query = (payload as { query?: unknown }).query;
      const target =
        typeof table === 'string' && table.trim() ? table : query ? 'custom query' : '';
      return target ? `Database ${operation}: ${target}` : `Database ${operation}`;
    }
    const artifactIdRaw = (payload as { artifact_id?: unknown }).artifact_id;
    if (typeof artifactIdRaw === 'string' && artifactIdRaw) {
      const normalizedId = artifactIdRaw.replace(/^artifact:/, '').replace(/:[0-9]+$/, '');
      const artifact = workspaceState.artifacts.find(
        (a) => a.id === artifactIdRaw || a.id === normalizedId,
      );
      if (artifact) return artifact.title;
      return artifactIdRaw;
    }
    if (typeof payload.url === 'string' && payload.url) return payload.url;
    const channel = (payload as { channel?: unknown }).channel;
    if (typeof channel === 'string' && channel) return `Slack to ${channel}`;
    const recordId = (payload as { record_id?: unknown }).record_id;
    if (typeof recordId === 'string' && recordId) return `Record ${recordId}`;
    return action.id;
  };

  const timelinePoints = timeline.data?.points ?? [];
  const isZoomed = Boolean(selectedDay);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">Metrics & logs</h1>
          <p className="text-sm text-neutral-600">
            Track LLM token usage and review the agent&apos;s action history for this workspace.
          </p>
        </div>

        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-4">
          {usage.data && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <InfoTile
                label="Total tokens"
                value={usage.data.totals.total_tokens.toLocaleString()}
              />
              <InfoTile
                label="Prompt tokens"
                value={usage.data.totals.prompt_tokens.toLocaleString()}
              />
              <InfoTile
                label="Completion tokens"
                value={usage.data.totals.completion_tokens.toLocaleString()}
              />
            </div>
          )}
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-neutral-800">
              {isZoomed ? `Usage details for ${selectedDay}` : 'Daily token usage'}
            </h3>
            <p className="text-xs text-neutral-500">
              {isZoomed
                ? 'Scatter view of all LLM calls on the selected day. Click empty space to return to the overview.'
                : 'Daily sums of total tokens. Scroll horizontally to explore history; click a point to zoom into that day.'}
            </p>
          </div>
          <div
            className="w-full"
            onClick={(event) => {
              if ((event.target as HTMLElement).dataset?.pointId == null && isZoomed) {
                setSelectedDay(null);
                setHoveredPointIndex(null);
              }
            }}
          >
            <div className="h-64 sm:h-72 lg:h-80">
              {timeline.isLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-neutral-500">
                  Loading usage timeline…
                </div>
              ) : !timelinePoints.length ? (
                <div className="flex h-full items-center justify-center text-sm text-neutral-500">
                  No usage data has been recorded yet.
                </div>
              ) : isZoomed ? (
                <DailyUsageScatterChart
                  day={selectedDay as string}
                  data={dailyUsage.data}
                  isLoading={dailyUsage.isLoading}
                  onPointHover={setHoveredPointIndex}
                  hoveredIndex={hoveredPointIndex}
                />
              ) : (
                <DailyUsageLineChart
                  points={timelinePoints}
                  onPointClick={(date) => {
                    setSelectedDay(date);
                    setHoveredPointIndex(null);
                  }}
                  onPointHover={setHoveredPointIndex}
                  hoveredIndex={hoveredPointIndex}
                />
              )}
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-4">
          <header className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">Action logs</h2>
              <p className="text-sm text-neutral-600">
                Review every action the agent has staged, approved, rejected, or executed.
              </p>
            </div>
            {isFetching && (
              <StatusBadge tone="neutral" className="text-xs">
                Refreshing…
              </StatusBadge>
            )}
          </header>

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="status-filter"
                className="text-xs font-semibold uppercase tracking-wide text-neutral-500"
              >
                Status
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilterValue)}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-800 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="actor-filter"
                className="text-xs font-semibold uppercase tracking-wide text-neutral-500"
              >
                Actor
              </label>
              <select
                id="actor-filter"
                value={actorFilter}
                onChange={(e) => setActorFilter(e.target.value)}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-800 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All actors</option>
                {actorOptions.map((actor) => (
                  <option key={actor} value={actor}>
                    {actor}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="type-filter"
                className="text-xs font-semibold uppercase tracking-wide text-neutral-500"
              >
                Action type
              </label>
              <select
                id="type-filter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-800 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All types</option>
                {typeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="search-filter"
                className="text-xs font-semibold uppercase tracking-wide text-neutral-500"
              >
                Search
              </label>
              <input
                id="search-filter"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search actions…"
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-800 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center px-6 py-12 text-sm text-neutral-500">
              Loading actions…
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center text-sm text-neutral-500">
              <p>We were unable to load the actions. Please try again.</p>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : filteredActions.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center text-sm text-neutral-500">
              <p>No actions match the current filters.</p>
              {(statusFilter !== 'all' ||
                actorFilter !== 'all' ||
                typeFilter !== 'all' ||
                normalizedSearch) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setActorFilter('all');
                    setTypeFilter('all');
                    setSearchTerm('');
                  }}
                >
                  Reset filters
                </Button>
              )}
            </div>
          ) : (
            <div className="border border-neutral-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        Action
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        Actors
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        Created
                      </th>
                      <th className="px-4 py-3" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200 bg-white">
                    {filteredActions.map((action) => {
                      const actors = getActionActors(action);
                      return (
                        <tr key={action.id} className="transition hover:bg-neutral-50">
                          <td className="px-4 py-3">
                            <div className="text-sm font-semibold text-neutral-900">
                              {getActionName(action)}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-neutral-400">
                              <span className="rounded-full border border-neutral-200 bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-500">
                                {action.action_type}
                              </span>
                              {action.requires_approval && (
                                <span className="inline-flex items-center gap-1">
                                  <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                                    Requires approval
                                  </span>
                                  <Tooltip content="This action must be approved by a human reviewer before it can execute.">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      aria-label="Why approval is required"
                                      className="h-5 w-5 !p-0"
                                      leftIcon={<HelpCircle className="h-3 w-3" />}
                                    />
                                  </Tooltip>
                                </span>
                              )}
                            </div>
                            {action.reasoning && (
                              <p className="mt-1.5 line-clamp-1 text-xs text-neutral-600">
                                {action.reasoning}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge tone={STATUS_TONES[action.status]}>
                              {STATUS_LABELS[action.status]}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-600">
                            {actors.join(', ')}
                          </td>
                          <td className="px-4 py-3 text-sm text-neutral-600">
                            {formatDateTime(action.created_at)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setSelectedAction(action)}
                            >
                              View audit log
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="bg-neutral-50 px-4 py-2 border-t border-neutral-200 text-xs text-neutral-500">
                Showing {filteredActions.length} of {data.length} actions
              </div>
            </div>
          )}
        </section>

        <Modal isOpen={selectedAction != null} onClose={() => setSelectedAction(null)} size="xl">
          {selectedAction && (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                  Action {selectedAction.id}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-neutral-900">Audit trail</h2>
                  <StatusBadge tone={STATUS_TONES[selectedAction.status]}>
                    {STATUS_LABELS[selectedAction.status]}
                  </StatusBadge>
                </div>
              </ModalHeader>
              <ModalBody className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1 text-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Action type
                    </p>
                    <p className="font-medium text-neutral-800">{selectedAction.action_type}</p>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Created by
                    </p>
                    <p className="font-medium text-neutral-800">
                      {selectedAction.created_by ?? FALLBACK_ACTOR}
                    </p>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Created at
                    </p>
                    <p className="font-medium text-neutral-800">
                      {formatDateTime(selectedAction.created_at)}
                    </p>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Updated at
                    </p>
                    <p className="font-medium text-neutral-800">
                      {formatDateTime(selectedAction.updated_at)}
                    </p>
                  </div>
                </div>
                {selectedAction.reasoning && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Reasoning
                    </p>
                    <p className="rounded-xl bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
                      {selectedAction.reasoning}
                    </p>
                  </div>
                )}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                      Audit log
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedAction(null)}>
                      Close
                    </Button>
                  </div>
                  <AuditLogTimeline events={selectedAction.audit_log} />
                </div>
              </ModalBody>
            </>
          )}
        </Modal>
      </div>
    </div>
  );
};
